<template>
  <div class="chat" :class="{ light }">
    <div class="chat__head">
      <chat-header
        :campaign="campaign"
        :channel="channel"
        :client="client"
        :last-message="lastMessage"
        :agentID="agentId"
        :favorite="isFavorite"
        :blocked="isBlocked"
      />
    </div>
    <div class="chat__body">
      <chat-conversation :client-id="clientId" :messages="messages" />
    </div>
    <div class="chat__footer">
      <chat-shortcuts />
      <chat-multimedia @send-message="prepareMessage" />
      <v-emoji-picker
        class="emoji"
        :emojisByRow="15"
        v-show="showEmojis"
        @select="selectEmoji"
      />
      <chat-preview
        :message="previewedMessage"
        @confirm="confirmMessage"
        @cancel="cancelPreview"
      />

      <chat-input
        ref="chatInput"
        :blocked="isBlocked"
        :disabled="isPreviewShowing || isBlocked"
        :client-id="clientId"
        :channel-type="channelType"
        @send-message="prepareMessage"
      />
      <input-lock v-if="isBlocked">
        <template #button>
          <div class="block-warning">
            {{ $t("dialog.block-warning") }}
          </div>
        </template>
      </input-lock>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex';
import { VEmojiPicker } from 'v-emoji-picker';
import {
  CHAT_EMOJI_TOGGLE,
  CHAT_IMAGE_FROM_CLIPBOARD,
  CHAT_SEND_MESSAGE,
} from '@/eventTypes';
import EventBus from '@/eventBus';
import InputLock from '@/components/InputLock.vue';

export default {
  components: {
    ChatConversation: () => import('@/components/ChatConversation.vue'),
    ChatHeader: () => import('@/components/ChatHeader.vue'),
    ChatInput: () => import('@/components/ChatInput.vue'),
    ChatMultimedia: () => import('@/components/ChatMultimedia.vue'),
    ChatPreview: () => import('@/components/ChatPreview.vue'),
    ChatShortcuts: () => import('@/components/ChatShortcuts.vue'),
    VEmojiPicker,
    InputLock,
  },
  data() {
    return {
      previewedMessage: null,
      showEmojis: false,
    };
  },
  computed: {
    ...mapState({
      conversations: (state) => state.conversations,
    }),
    ...mapGetters([
      'activeConversation',
      'userInfo',
      'getConfig',
      'theme',
    ]),
    channel() {
      return this.activeConversation && this.activeConversation.conversation
        ? this.activeConversation.conversation.channel ?? {}
        : {};
    },
    campaign() {
      return this.activeConversation && this.activeConversation.conversation
        ? this.activeConversation.conversation.campaign ?? {}
        : {};
    },
    campaignId() {
      return this.campaign.id || '';
    },
    channelId() {
      return this.channel.id || '';
    },
    channelType() {
      return this.channel.channelType.key ?? '';
    },
    client() {
      return this.activeConversation
        ? this.activeConversation.client ?? {}
        : {};
    },
    clientId() {
      return this.client.id || '';
    },
    messages() {
      return this.activeConversation
        ? this.activeConversation.messages ?? []
        : [];
    },
    lastMessage() {
      if (this.messages.length > 0) {
        return this.messages[this.messages.length - 1];
      }

      return {};
    },
    agentId() {
      return this.activeConversation ? this.activeConversation.agentId : '';
    },
    isFavorite() {
      let isFavorite = false;
      const { activeConversation } = this;

      if (activeConversation) {
        const { favorites } = activeConversation.client;

        isFavorite = favorites.some(
          (fave) => fave.campaign === activeConversation.conversation.campaign.id
            && fave.user === this.userInfo.id,
        );
      }

      return isFavorite;
    },
    isBlocked() {
      let isBlocked = false;
      const { activeConversation } = this;

      if (activeConversation) {
        const { blocked } = activeConversation.client;
        isBlocked = blocked && blocked.some(
          (block) => block.channel === activeConversation.conversation.channel.id
            && block.user === activeConversation.agentId,
        );
      }
      return isBlocked;
    },
    isPreviewShowing() {
      return this.previewedMessage !== null;
    },
    configuration() {
      return this.getConfig(this.campaignId, this.channelId);
    },
    blockUsersEnabled() {
      return this.configuration.blockUsersEnabled ?? false;
    },
    light() {
      return this.theme === 'light';
    },
  },
  watch: {
    clientId() {
      this.cancelPreview();
    },
  },
  created() {
    EventBus.$on(CHAT_EMOJI_TOGGLE, (opened) => {
      this.showEmojis = opened;
    });

    EventBus.$on(CHAT_IMAGE_FROM_CLIPBOARD, (payload) => {
      this.prepareMessage(payload);
    });
  },
  destroyed() {
    EventBus.$off(CHAT_EMOJI_TOGGLE);
  },
  methods: {
    ...mapMutations(['removeDataBankConfiguration']),
    hasPreview(type) {
      return !['text', 'tText'].includes(type);
    },
    prepareMessage(basicMsg) {
      this.previewedMessage = null;

      const msg = basicMsg.message || basicMsg;
      const campaign = basicMsg.campaign || this.campaignId;

      const payload = {
        ...msg,
        tempId: this.uuidv4(),
        typeUser: 'User',
        user: {
          id: this.userInfo.id,
        },
      };

      const message = this.buildMessage(this.clientId, this.channelId, campaign, payload);

      if (this.hasPreview(message.type)) {
        this.previewedMessage = {
          ...message,
          filename: msg.filename,
        };
      } else {
        this.sendMessage(message);
      }
    },
    confirmMessage() {
      this.sendMessage(this.previewedMessage);
    },
    sendMessage(msg) {
      EventBus.$emit(CHAT_SEND_MESSAGE, msg);
      this.cancelPreview();
    },
    cancelPreview(deleteElement) {
      if (deleteElement) {
        const { link, type } = this.previewedMessage;
        this.removeDataBankConfiguration({ link, type });
      }
      this.previewedMessage = null;
    },
    selectEmoji(emoji) {
      this.$refs.chatInput.addEmoji(emoji.data);
    },
  },
};
</script>

<style lang="scss">
.emoji {
  width: 100% !important;

  .container-emoji {
    max-height: 200px;
  }
}

.block-warning {
  padding: 10px;
  margin: 0 20px 8px;
  border-radius: 4px;
  background-color: lighten($alert-red-alt, 20%);
  font-weight: bold;
  color: $alert-red;
  text-align: center;
  transition: all ease 0.5s;
  width: 95%;
}
</style>
